import { HttpError, file2Base64 } from '@pankod/refine-core';
import {
  Avatar,
  Col,
  Create,
  Form,
  getValueFromEvent,
  Input,
  Row,
  Select,
  Space,
  useForm,
  Upload,
  notification,
} from '@pankod/refine-antd';
import { ChangeEvent, useEffect, useState } from 'react';
import { IClient, ITag } from 'interfaces';
import { AddressFormItem, CustomModal } from 'components';
import { getAllTag } from 'services/tag';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import avatarPlaceholder from 'assets/images/avatar-placeholder.png';
import { generateUniqueIdFromString } from 'utilities/shared';

const { Option } = Select;

export const ClientCreate: React.FC = () => {
  const [tags, setTags] = useState<ITag[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [clientUniqueCode, setClientUniqueCode] = useState<string>();
  const [countryCode, setCountryCode] = useState<string>('+88');

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllTag();
        const tagsData = data.data;
        setTags(tagsData);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
      }
    })();
  }, []);

  const { form, formProps, saveButtonProps, queryResult } = useForm<IClient, HttpError>({
    metaData: {
      fields: ['id'],
    },
  });

  const countryCodeSelector = (
    <Form.Item name="countryCode" noStyle>
      <Select
        style={{ width: 70 }}
        defaultValue={countryCode}
        value={countryCode}
        onChange={setCountryCode}
      >
        <Option value="+88">+88</Option>
      </Select>
    </Form.Item>
  );

  const getTagOptions = () => {
    return tags.map((tag) => (
      <Option key={tag.id} value={tag.id}>
        {tag.name}
      </Option>
    ));
  };

  const createClient = async (values: any) => {
    const { avatar, tags } = values;
    const base64Files = [];

    if (avatar && avatar.length) {
      const file = avatar[0];
      const format = file.type || '';
      const size = file.size || 0;

      if (
        isJpgOrPngFile(format, file.name || '') &&
        isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
      ) {
        if (file.originFileObj) {
          const base64String = await file2Base64(file);
          const { originFileObj, ...rest } = file;
          base64Files.push({ ...rest, base64String });
        } else {
          base64Files.push(file);
        }
      }
    }

    const tagsPayload = !!tags ? tags.map((tag: string) => ({ id: tag })) : [];

    formProps.onFinish &&
      formProps.onFinish({
        ...values,
        tags: tagsPayload,
        avatar: base64Files,
      });
  };

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    const generatedUniqueId = generateUniqueIdFromString(name);
    setClientUniqueCode(generatedUniqueId);
  };

  const onFinish = async (values: any) => {
    const { clientCode } = values;
    if (!clientCode) {
      showModal();
      return;
    }
    return createClient({ ...values, countryCode });
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.setFieldsValue({
      clientCode: clientUniqueCode,
    });
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    form.setFields([
      {
        name: 'clientCode',
        errors: [`Please provide your unique id.`],
      },
    ]);
    setIsModalVisible(false);
  };

  return (
    <Create isLoading={queryResult?.isFetching} saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        form={form}
        layout="vertical"
        initialValues={{
          isActive: true,
        }}
        onFinish={onFinish}
      >
        <Row gutter={[56, 0]} wrap>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '16px',
                marginTop: '6px',
              }}
            >
              <Form.Item
                name="avatar"
                valuePropName="fileList"
                getValueFromEvent={getValueFromEvent}
                noStyle
              >
                <Upload.Dragger
                  listType="picture"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  maxCount={1}
                  beforeUpload={beforeImageUploadValidation}
                >
                  <Avatar
                    size={200}
                    style={{ objectFit: 'cover' }}
                    src={avatarPlaceholder}
                    alt="Store Location"
                  />
                  <p className="ant-upload-text">Drag & drop an image file in this area</p>
                </Upload.Dragger>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label="Client Name"
              name="name"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input onChange={onNameChange} />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  type: 'email',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Mobile Number"
              name="mobileNumber"
              rules={[
                {
                  required: true,
                },
                {
                  pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                  message: 'Provide valid number',
                },
              ]}
            >
              <Input addonBefore={countryCodeSelector} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Unique ID"
              name="clientCode"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select
                showSearch
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select tags"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {getTagOptions()}
              </Select>
            </Form.Item>
            <AddressFormItem />
          </Col>
        </Row>
      </Form>
      <CustomModal
        title="Missing required fields!"
        isModalVisible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>
          You have not provided some required information for this action. These data will be
          auto-generated:
        </p>
        <p>
          <strong>Unique ID: </strong>
          {clientUniqueCode}
        </p>
      </CustomModal>
    </Create>
  );
};
